import type { QueryClient } from "@tanstack/vue-query";
import type { MutationRouteName, QueryRouteName } from "./query-keys.ts";

/**
 * Which reads a change makes stale (ADR-076 point 4). The type demands an entry for every route
 * that changes something, so a new route in the catalogue does not compile until it is listed
 * here. The lists follow what the change touches in the response schemas, the timeline included,
 * since every change writes an activity (R-10). Logout clears the whole cache instead.
 */
export const invalidates = {
  loginStart: [],
  logout: [],
  inquiryCreate: [],

  orderCreate: ["orderList", "entitlementList", "products", "history"],
  orderCancel: ["orderList", "orderGet", "entitlementList", "products", "history"],
  monthlyLimitSet: ["entitlementList", "usageSummary", "history"],
  demoRequest: ["orderList", "entitlementList", "products", "history"],

  // a new customer appears in the list (UC-20), its file and timeline are fresh like after every
  // staff change; an order by the team in the order list and the customer's file with its
  // timeline (UC-32, ADR-133)
  staffCustomerCreate: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline"],
  staffOrderCreate: ["staffOrderList", "staffCustomerGet", "staffCustomerTimeline"],
  staffCustomerApprove: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline"],
  staffCreditLimitSet: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline"],
  // suspend, reinstate and the instance link change the customer's state in list and file and
  // write an activity (AP-B8.5); a provisioning rests under the suspension, so the tasks show it
  // (ADR-128); the role change touches the file's users and the timeline
  staffCustomerSuspend: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline", "staffTaskList"],
  staffCustomerReinstate: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline", "staffTaskList"],
  staffCustomerInstance: ["staffCustomerList", "staffCustomerGet", "staffCustomerTimeline"],
  staffUserRole: ["staffCustomerGet", "staffCustomerTimeline"],
  // the revocation ends the entitlement and writes the remove task (R-15); the rotation only
  // records the activity, the key summary stays until the worker ran. Revocation, termination,
  // rotation and a completed task change which key OpenRouter reads, so the usage in the file is
  // read again (ADR-145)
  staffEntitlementRevoke: ["staffCustomerGet", "staffCustomerTimeline", "staffTaskList", "staffCustomerUsageSummary"],
  // the termination ends the entitlement like the revocation (UC-16, ADR-137)
  staffEntitlementTerminate: [
    "staffCustomerGet",
    "staffCustomerTimeline",
    "staffTaskList",
    "staffCustomerUsageSummary",
  ],
  staffKeyRotate: ["staffCustomerGet", "staffCustomerTimeline", "staffCustomerUsageSummary"],
  // the team's monthly limit (ADR-160 point F12.4-3, AP-B14-13): the file's table and figures (the
  // limit sum, the available amount, the budget), its timeline and the usage per product with its
  // limit; the customer list shows no limit
  staffMonthlyLimitSet: ["staffCustomerGet", "staffCustomerTimeline", "staffCustomerUsageSummary"],
  // the order on its own page too, the mails' direct link (ADR-135 Nachtrag)
  staffOrderStatus: ["staffOrderList", "staffOrderGet", "staffTaskList", "staffCustomerGet", "staffCustomerTimeline"],
  staffOrderInvoice: ["staffOrderList", "staffOrderGet", "staffCustomerGet", "staffCustomerTimeline"],
  staffTaskDone: [
    "staffTaskList",
    "staffOrderList",
    "staffOrderGet",
    "staffCustomerGet",
    "staffCustomerTimeline",
    "staffCustomerUsageSummary",
  ],
  staffTaskFail: ["staffTaskList", "staffCustomerGet", "staffCustomerTimeline"],
  staffKeyReveal: ["staffCustomerGet", "staffCustomerTimeline"],
  staffUsageReportFreeze: ["staffUsageReports", "staffCustomerGet", "staffCustomerTimeline"],
  // a correction and an invoice are entries of the customer's ledger (ADR-151)
  staffCorrectionBook: ["staffUsageReports", "staffCustomerGet", "staffCustomerTimeline", "staffCustomerLedger"],
  staffInvoiceBook: ["staffUsageReports", "staffCustomerGet", "staffCustomerTimeline", "staffCustomerLedger"],
  // the list's key covers the customer's inquiries in the file too, the same route with a filter
  staffInquiryUpdate: ["staffInquiryList", "staffInquiryGet"],
  // products belong to no customer: no activity, no timeline; the staff list, the catalogue, which
  // the sidebar shows as well (UC-26, N16), and the history of the terms (ADR-125 Nachtrag)
  staffProductTerms: ["staffProductList", "products", "staffProductHistory"],
  // a deleted product leaves the staff list; it was unpublished, so the catalogue never showed it (ADR-148)
  staffProductDelete: ["staffProductList"],
} as const satisfies Record<MutationRouteName, readonly QueryRouteName[]>;

/** Marks every read listed for `name` as stale; mounted queries refetch at once. */
export async function invalidateAfter(queryClient: QueryClient, name: MutationRouteName): Promise<void> {
  const stale: readonly QueryRouteName[] = invalidates[name];
  await Promise.all(stale.map((route) => queryClient.invalidateQueries({ queryKey: [route] })));
}
